import { Box, Paper, Typography } from '@mui/material';
import {
  BASIC_BORDER_COLOR,
  KPI_CAUTION_BADGE_COLOR,
  KPI_CRITICAL_BADGE_COLOR,
  KPI_OPTIMAL_BADGE_COLOR,
} from '../../../../themes/tokens';
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import { useTranslation } from 'react-i18next';
import { CustomBarWithBenchmark } from './CustomBarWithBenchmark';
import { WrappedAxisTick } from './WrappedxAxisTick';
import { KpisByPlanChartData } from './types';
import { normalizeBarValue } from './helpers';

const PREVIOUS_BAR_COLOR = '#B3C0B5';

type Props = {
  tooltipPreviousCopy: string;
  data: KpisByPlanChartData[];
  planTitle?: string;
};

type ChartData = KpisByPlanChartData & {
  name: string;
  showBenchmark?: boolean;
  showPrevious?: boolean;
  previousRaw?: number | null;
};

export const KpisByPlanChart = ({ planTitle, data, tooltipPreviousCopy }: Props) => {
  const { t } = useTranslation();

  const chartData: ChartData[] = data.map((item) => {
    return {
      ...item,
      name: t(`dashboard.kpi.${item.field}`),
      showBenchmark: true,
      showPrevious: true,
      tooltipPreviousCopy: tooltipPreviousCopy,
      value: normalizeBarValue(item.value),
      previousRaw: item.previous,
      previous: normalizeBarValue(item.previous),
    };
  });

  const getBarColor = (item: ChartData) => {
    if (item.benchmarkMax && item.value > item.benchmarkMax) return KPI_OPTIMAL_BADGE_COLOR;
    if (item.benchmarkMin && item.value < item.benchmarkMin) return KPI_CRITICAL_BADGE_COLOR;
    return KPI_CAUTION_BADGE_COLOR;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        aspectRatio: 1,
        height: '100%',
        p: 2,
        borderRadius: '8px',
        bgcolor: '#f8f7f6',
        border: `1px solid ${BASIC_BORDER_COLOR}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '10px',
        padding: '16px 8px',
        position: 'relative',
      }}
    >
      <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%', height: '100%' }}>
        <Typography variant="h6" textAlign={'left'} marginLeft={'20px'}>
          {planTitle}
        </Typography>
        <Box sx={{ width: '100%', height: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ left: 5, right: 5, top: 35 }}
              barCategoryGap={2}
              barGap={0}
            >
              <XAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={WrappedAxisTick}
              />
              <YAxis
                type="number"
                width={25}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                ticks={[0, 20, 40, 60, 80, 100]}
                tick={{ fontSize: 11 }}
              />

              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<CustomTooltip />} />
              <CartesianGrid horizontal vertical={false} strokeDasharray="1" />

              <Bar dataKey="previous" fill={PREVIOUS_BAR_COLOR} barSize={30} />
              <Bar
                dataKey="value"
                barSize={30}
                shape={(props: any) => <CustomBarWithBenchmark {...props} />}
              >
                {chartData.map((item) => (
                  <Cell key={item.field} fill={getBarColor(item)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
};
